/**
 * Price Utilities
 * 
 * FRAME token price in USD - REAL prices only, no hardcoded fallbacks.
 * Users need accurate prices to make spending decisions.
 */

// Price API endpoint
const PRICE_API_URL = 'https://nt3d-price-api.wurkit.workers.dev/api/price/paw';

// wFRAME token on mainnet for direct DexScreener lookup
const WFRAME_TOKEN_ADDRESS = '0xc11158c5dA9db1D553ED28f0C2BA1CbEDD42CFcb';

// Cache
let cachedPrice: number = 0;
let lastFetchTime: number = 0;
const CACHE_DURATION = 30000; // 30 seconds - match the API cache

// Track if we've ever successfully fetched a price
let hasFetchedPrice = false;

export interface WPAWPriceData {
  price: number;
  change24h: number;
  volume24h: number;
  source?: string;
}

let cachedWPAWData: WPAWPriceData = { price: 0, change24h: 0, volume24h: 0 };
let lastWPAWFetchTime: number = 0;

/** $FRAME has not launched: no price, no USD conversions. */
export const FRAME_PRELAUNCH = true;

/**
 * Fetch FRAME price with 24h change.
 */
export async function fetchWPAWPriceData(): Promise<WPAWPriceData> {
  const now = Date.now();
  
  if (now - lastWPAWFetchTime < CACHE_DURATION && cachedWPAWData.price > 0) {
    return cachedWPAWData;
  }
  
  const price = await fetchPAWPrice();
  cachedWPAWData = { price, change24h: 0, volume24h: 0, source: 'dexscreener' };
  lastWPAWFetchTime = now;
  
  return cachedWPAWData;
}

/**
 * Fetch FRAME price in USD.
 * Uses our price-api worker first, falls back to direct DexScreener.
 * Returns 0 if price unavailable - NO HARDCODED FALLBACK.
 */
export async function fetchPAWPrice(): Promise<number> {
  // PRE-LAUNCH: $FRAME has not launched and has no price. Never show a price or USD value.
  // Remove this early return only once $FRAME has actually launched.
  if (FRAME_PRELAUNCH) return 0;
  const now = Date.now();
  
  // Use cache if fresh and valid
  if (now - lastFetchTime < CACHE_DURATION && cachedPrice > 0) {
    return cachedPrice;
  }
  
  // Try our price API first
  try {
    const response = await fetch(PRICE_API_URL, {
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(5000)
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.price && data.price > 0) {
        cachedPrice = data.price;
        lastFetchTime = now;
        hasFetchedPrice = true;
        console.log(`FRAME price from API: $${data.price} (source: ${data.source || 'unknown'}, chain: ${data.chain || 'unknown'})`);
        return cachedPrice;
      }
    }
  } catch (error) {
    console.warn('Price API failed, trying direct DexScreener:', error);
  }
  
  // Fallback: Direct DexScreener call
  try {
    const dexPrice = await fetchPAWPriceFromDexScreener();
    if (dexPrice > 0) {
      cachedPrice = dexPrice;
      lastFetchTime = now;
      hasFetchedPrice = true;
      return cachedPrice;
    }
  } catch (error) {
    console.error('DexScreener fallback also failed:', error);
  }
  
  // Return cached price if we have one (even if stale), otherwise 0
  if (cachedPrice > 0) {
    console.warn('Using stale cached price:', cachedPrice);
    return cachedPrice;
  }
  
  console.error('FRAME price unavailable - no valid source');
  return 0;
}

/**
 * Fetch FRAME price directly from DexScreener.
 * Priority: Ethereum > BSC > Polygon > Base (by liquidity)
 */
export async function fetchPAWPriceFromDexScreener(): Promise<number> {
  const CHAIN_PRIORITY = ['ethereum', 'bsc', 'polygon', 'base'];
  
  try {
    const response = await fetch(
      `https://api.dexscreener.com/latest/dex/tokens/${WFRAME_TOKEN_ADDRESS}`,
      {
        headers: { 'Accept': 'application/json' },
        signal: AbortSignal.timeout(5000)
      }
    );
    
    if (!response.ok) {
      throw new Error(`DexScreener returned ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data?.pairs?.length) {
      throw new Error('No trading pairs found');
    }
    
    // Filter pairs with valid prices
    const validPairs = data.pairs.filter((pair: any) => 
      pair.priceUsd && parseFloat(pair.priceUsd) > 0
    );
    
    if (validPairs.length === 0) {
      throw new Error('No pairs with valid price');
    }
    
    // Sort by chain priority, then by liquidity
    validPairs.sort((a: any, b: any) => {
      const aPriority = CHAIN_PRIORITY.indexOf(a.chainId);
      const bPriority = CHAIN_PRIORITY.indexOf(b.chainId);
      
      if (aPriority !== bPriority) {
        if (aPriority === -1) return 1;
        if (bPriority === -1) return -1;
        return aPriority - bPriority;
      }
      
      return (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0);
    });
    
    const bestPair = validPairs[0];
    const price = parseFloat(bestPair.priceUsd);
    
    console.log(`DexScreener: ${bestPair.chainId}/${bestPair.dexId} - $${price} (liq: $${bestPair.liquidity?.usd?.toLocaleString() || 'N/A'})`);
    return price;
    
  } catch (error) {
    console.error('DexScreener price fetch failed:', error);
    throw error;
  }
}

/**
 * Check if we have a valid price (has been fetched at least once)
 */
export function hasValidPrice(): boolean {
  return hasFetchedPrice && cachedPrice > 0;
}

/**
 * Get the current cached price without fetching
 */
export function getCachedPrice(): number {
  return cachedPrice;
}

/**
 * Format FRAME amount to USD string
 */
export function formatFRAMEToUSD(pawAmount: bigint, framePrice: number): string {
  if (framePrice <= 0) {
    return '--'; // No price available
  }
  
  const pawNumber = Number(pawAmount) / 1e18;
  const usdValue = pawNumber * framePrice;
  
  if (usdValue >= 1e12) {
    return (usdValue / 1e12).toFixed(2) + 'T';
  } else if (usdValue >= 1e9) {
    return (usdValue / 1e9).toFixed(2) + 'B';
  } else if (usdValue >= 1e6) {
    return (usdValue / 1e6).toFixed(2) + 'M';
  } else if (usdValue >= 1000) {
    return (usdValue / 1000).toFixed(2) + 'K';
  } else if (usdValue >= 1) {
    return usdValue.toFixed(2);
  } else if (usdValue >= 0.01) {
    return usdValue.toFixed(4);
  } else if (usdValue > 0) {
    return usdValue.toFixed(6);
  } else {
    return '0.00';
  }
}


